import { memo } from 'react';

export interface WysiwygJsonReferenceProps {
  onClose: () => void;
}

/**
 * Side panel that documents the full JSON shape used by the form definition.
 * Mirrors what the Design tab can produce — types, properties, per-field
 * options, showIf, layout flags, formulas. Anything you can build in the
 * Design tab has a JSON equivalent here.
 *
 * Lives next to the textarea on wide screens so the user can scroll
 * through the reference while editing the JSON.
 */
export const WysiwygJsonReference = memo(function WysiwygJsonReference({
  onClose,
}: WysiwygJsonReferenceProps) {
  return (
    <aside className="wysiwyg-json-reference" aria-label="JSON reference">
      <div className="wysiwyg-json-reference-header">
        <span className="wysiwyg-json-reference-title">JSON reference</span>
        <button
          type="button"
          className="wysiwyg-inspector-close"
          onClick={onClose}
          aria-label="Close reference"
        >×</button>
      </div>

      <div className="wysiwyg-json-reference-body">

        <Section title="Form (top level)">
          <p>
            A form is an object with a name, what record type it attaches to, and a list of
            sections. Top-level fields:
          </p>
          <PropList rows={[
            ['name', 'string', 'Required. Shown to admins in the Custom Forms list.'],
            ['attachTo', '"CLIENT" or "CASE_REPORT"', 'Required. The kind of record the form is filled out against: a client record or a case report.'],
            ['sections', 'array', 'Required. One or more section objects (see below).'],
          ]} />
          <Code>{`{
  "name": "House Check Request",
  "attachTo": "CLIENT",
  "sections": [
    { ... },
    { ... }
  ]
}`}</Code>
        </Section>

        <Section title="Section">
          <p>Each section becomes a titled card on the form.</p>
          <PropList rows={[
            ['id', 'string', 'Required. Unique within the form. snake_case is conventional.'],
            ['title', 'string', 'Required. Shown as the section header.'],
            ['fields', 'array', 'Required. The fields inside the section.'],
            ['sidebar', 'boolean', 'Optional. When true, the section renders as a narrow card on the right of the form (on wide screens). Use for short summary cards like billing totals.'],
          ]} />
          <Code>{`{
  "id": "emergency_contact",
  "title": "Emergency Contact",
  "fields": [ ... ]
}`}</Code>
        </Section>

        <Section title="Field — properties shared by every type">
          <PropList rows={[
            ['key', 'string', 'Required. Unique within the form. The internal name a value is stored under. snake_case is conventional.'],
            ['label', 'string', 'Required. Shown above (or beside) the input.'],
            ['type', 'one of the type strings below', 'Required. Determines what control is rendered.'],
            ['required', 'boolean', 'Optional. Form can\'t be submitted without a value.'],
            ['searchable', 'boolean', 'Optional. Lets staff filter records by this field in Records Search. Not allowed on tables, and not available on case report forms.'],
            ['summary', 'boolean', 'Optional. Show this field on the tour stop summary. Not allowed on tables.'],
            ['tab', 'boolean', 'Optional. Show this field on the live tour stop detail page in an extra tab. A tab is added automatically once any field on the form has this set. Not allowed on tables.'],
            ['wide', 'boolean', 'Optional. Span the full width of the section instead of taking half.'],
            ['subheading', 'string', 'Optional. Renders a small heading + divider above this field. Use "" for a divider with no text.'],
            ['inlineWithPrevious', 'boolean', 'Optional. Place this field on the same row as the field above it.'],
            ['showIf', 'object', 'Optional. See "Conditional visibility" below.'],
          ]} />
        </Section>

        <Section title='type: "text"'>
          <p>A short, single-line text input.</p>
          <Code>{`{ "key": "owner_name", "label": "Owner Name", "type": "text", "required": true }`}</Code>
        </Section>

        <Section title='type: "textarea"'>
          <p>A multi-line text input. Always full-width.</p>
          <Code>{`{ "key": "notes", "label": "Notes", "type": "textarea" }`}</Code>
        </Section>

        <Section title='type: "number"'>
          <p>A numeric input. Stored as a number.</p>
          <PropList rows={[
            ['format', '"currency"', 'Optional. Display the value as $X.XX (US dollars).'],
            ['layout', '"horizontal"', 'Optional. Label on the left, value on the right (works well for prices/counts).'],
          ]} />
          <Code>{`{ "key": "weeks", "label": "Number of Weeks", "type": "number" }
{ "key": "rate",  "label": "Weekly Rate",     "type": "number", "format": "currency" }`}</Code>
        </Section>

        <Section title='type: "date"'>
          <p>A calendar-date picker. Stored as YYYY-MM-DD.</p>
          <Code>{`{ "key": "start_date", "label": "Start Date", "type": "date" }`}</Code>
        </Section>

        <Section title='type: "phone"'>
          <p>A phone number input.</p>
          <Code>{`{ "key": "contact_phone", "label": "Contact Phone", "type": "phone" }`}</Code>
        </Section>

        <Section title='type: "email"'>
          <p>An email address input.</p>
          <Code>{`{ "key": "contact_email", "label": "Contact Email", "type": "email" }`}</Code>
        </Section>

        <Section title='type: "radio" — single choice'>
          <p>One choice from a list of mutually exclusive options.</p>
          <PropList rows={[
            ['options', 'array of {value, label}', 'Required. value is what gets stored; label is what people see on the form.'],
          ]} />
          <Code>{`{
  "key": "key_holder_present",
  "label": "Key Holder Present?",
  "type": "radio",
  "options": [
    { "value": "yes", "label": "Yes" },
    { "value": "no",  "label": "No" }
  ]
}`}</Code>
        </Section>

        <Section title='type: "select" — dropdown'>
          <p>Same shape as radio, rendered as a dropdown. Better for longer lists.</p>
          <Code>{`{
  "key": "alarm_company",
  "label": "Alarm Company",
  "type": "select",
  "options": [
    { "value": "adt",   "label": "ADT" },
    { "value": "vivint","label": "Vivint" },
    { "value": "other", "label": "Other" }
  ]
}`}</Code>
        </Section>

        <Section title='type: "checkboxGroup" — checkboxes'>
          <p>Pick any number of options. Stored as an array of selected values.</p>
          <Code>{`{
  "key": "services_in_use",
  "label": "Services in Use",
  "type": "checkboxGroup",
  "options": [
    { "value": "lawn", "label": "Lawn Care" },
    { "value": "pool", "label": "Pool / Spa" },
    { "value": "pest", "label": "Pest Control" }
  ]
}`}</Code>
        </Section>

        <Section title='type: "table"'>
          <p>
            A repeating row of fields. Each column is itself a field definition (no nested
            tables, no calculated columns). By default, anyone filling out the form can add
            and remove rows. Use predefinedRows to lock rows in place.
          </p>
          <PropList rows={[
            ['columns', 'array of fields', 'Required. Each column uses the same field shape as a top-level field, minus searchable/summary.'],
            ['predefinedRows', 'array of {id, label}', 'Optional. When set, the form shows these rows already in place and only the cells are editable. id must be unique inside the table.'],
          ]} />
          <Code>{`{
  "key": "incidents",
  "label": "Incidents",
  "type": "table",
  "columns": [
    { "key": "when",  "label": "When",  "type": "date" },
    { "key": "count", "label": "Count", "type": "number" },
    { "key": "note",  "label": "Note",  "type": "text" }
  ]
}

{
  "key": "services",
  "label": "Services in Use",
  "type": "table",
  "predefinedRows": [
    { "id": "lawn", "label": "Lawn Care" },
    { "id": "pool", "label": "Pool / Spa" }
  ],
  "columns": [
    { "key": "active",   "label": "Active?",
      "type": "radio",
      "options": [
        { "value": "y", "label": "Yes" },
        { "value": "n", "label": "No" }
      ]
    },
    { "key": "provider", "label": "Provider", "type": "text" }
  ]
}`}</Code>
        </Section>

        <Section title='type: "computed" — calculated'>
          <p>
            A read-only value calculated from earlier number/calculated fields. The formula is
            a math expression. Reference earlier fields by their key.
          </p>
          <PropList rows={[
            ['formula', 'string', 'Required. Math expression. + - * / and parentheses, plus references to earlier number/calculated fields by key. Missing values and divide-by-zero return 0.'],
            ['format', '"currency"', 'Optional. Display the result as $X.XX.'],
            ['layout', '"horizontal"', 'Optional. Label on the left, value on the right.'],
          ]} />
          <Code>{`{
  "key": "subtotal",
  "label": "Subtotal",
  "type": "computed",
  "formula": "weeks * rate",
  "format": "currency",
  "layout": "horizontal"
}`}</Code>
          <p className="wysiwyg-json-reference-help">
            Limits: at most 500 characters, at most 50 field references. Text, dates,
            comparisons, and built-in functions are not supported.
          </p>
        </Section>

        <Section title="Conditional visibility (showIf)">
          <p>
            Add a <code>showIf</code> object to hide a field until an earlier field has a
            specific value.
          </p>
          <PropList rows={[
            ['key', 'string', 'Required. The key of an earlier field in the same form.'],
            ['equals', 'string | number | boolean | null', 'Required. The value the earlier field must hold for this field to render. For radio/select/checkbox options, use the option\'s value (not its label).'],
          ]} />
          <Code>{`{
  "key": "pet_count",
  "label": "How many pets?",
  "type": "number",
  "showIf": { "key": "has_pets", "equals": "yes" }
}`}</Code>
          <p className="wysiwyg-json-reference-help">
            When a field is hidden, its value is cleared on save. The "Show all fields while editing"
            toggle in the Design tab keeps these visible in the editor.
          </p>
        </Section>

        <Section title="Layout flags">
          <PropList rows={[
            ['wide', 'boolean', 'Make a single field span the full row (full width). Useful for fields whose content is naturally long.'],
            ['layout', '"horizontal"', 'Put the label on the left and value on the right. Good for short numeric fields like prices or counts in a billing card.'],
            ['subheading', 'string', 'Render a small heading and divider above this field, useful for grouping fields visually inside one section. Use "" for just the divider with no text.'],
            ['inlineWithPrevious', 'boolean', 'Place this field on the same row as the field above. Good for "question + follow-up" patterns like "Lights on timer? [Yes/No] Locations: [text]".'],
          ]} />
        </Section>

        <Section title="Validation rules">
          <ul className="wysiwyg-json-reference-list">
            <li>Field <code>key</code> values must be unique within the form (and inside any single table's columns).</li>
            <li>Section <code>id</code> values must be unique within the form.</li>
            <li>The form must have at least one section, and at least one field overall.</li>
            <li><code>radio</code>, <code>select</code>, <code>checkboxGroup</code> require non-empty <code>options</code>. Each option's <code>value</code> must be unique within that field.</li>
            <li><code>table</code> requires at least one column. Columns can be any scalar field type (no nested tables, no calculated columns). Tables don't support <code>searchable</code> or <code>summary</code>.</li>
            <li><code>computed</code> requires <code>formula</code>. Formulas can only reference earlier <code>number</code> or <code>computed</code> fields.</li>
            <li><code>showIf.key</code> must reference an earlier field in the same form (cannot reference fields after this one or fields in later sections).</li>
          </ul>
        </Section>

      </div>
    </aside>
  );
});

interface SectionProps {
  title: string;
  children: React.ReactNode;
}
function Section({ title, children }: SectionProps) {
  // NOTE: must be <div>, not <section>. Legacy `Content/app.css` has a
  // global `section { display: none; }` rule used as a conditional-show
  // mechanism by the legacy SPA — anything wrapped in <section> would
  // silently disappear here.
  return (
    <div className="wysiwyg-json-reference-section">
      <h4 className="wysiwyg-json-reference-section-title">{title}</h4>
      {children}
    </div>
  );
}

interface PropListProps {
  rows: [string, string, string][];
}
function PropList({ rows }: PropListProps) {
  return (
    <table className="wysiwyg-json-reference-proplist">
      <tbody>
        {rows.map(([name, type, notes], i) => (
          <tr key={i}>
            <td className="wysiwyg-json-reference-proplist-name"><code>{name}</code></td>
            <td className="wysiwyg-json-reference-proplist-type"><code>{type}</code></td>
            <td>{notes}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function Code({ children }: { children: React.ReactNode }) {
  return <pre className="wysiwyg-json-reference-code">{children}</pre>;
}
